import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import ApiService from "../api/apiService";
import showSwal from "./swal";
import { dateToWords, getFileKind } from "../util/helperFunctions";
import { common } from "../util/common";
import ModalShell from "./modal/ModalShell";
import UserAvatar from "./modal/UserAvatar";
import { EmptyState, SkeletonRows } from "./modal/EmptyState";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "image", label: "Images" },
  { key: "video", label: "Videos" },
  { key: "doc", label: "Documents" },
];

const KIND_ICON = {
  pdf: "fa-file-pdf-o",
  doc: "fa-file-word-o",
  sheet: "fa-file-excel-o",
  slide: "fa-file-powerpoint-o",
  archive: "fa-file-archive-o",
  audio: "fa-file-audio-o",
  video: "fa-file-video-o",
  image: "fa-file-image-o",
  file: "fa-file-o",
};

const fileUrl = (name) => `${process.env.REACT_APP_CHAT_RESOURCES_URL}/${name}`;
const PAGE_SIZE = 30;
const SCROLL_TARGET_ID = "view-files-scrollable-body";
const monthLabel = (date) =>
  isNaN(date) ? "Unknown date" : date.toLocaleString("en-US", { month: "long", year: "numeric" });
// show the original name without any "timestamp-" style upload prefix when it is obvious
const displayName = (name) => String(name || "").replace(/^\d{8,}[-_]/, "");

function FileThumb({ item }) {
  const [broken, setBroken] = useState(false);
  const { kind } = item;
  const src = fileUrl(item.attachment);

  if (kind === "image" && !broken) {
    return <img src={src} alt='' loading='lazy' onError={() => setBroken(true)} />;
  }
  if (kind === "video" && !broken) {
    return (
      <>
        <video src={`${src}#t=0.1`} preload='metadata' muted playsInline onError={() => setBroken(true)} />
        <i className='fa fa-play-circle bh-file__play' aria-hidden='true'></i>
      </>
    );
  }
  return <i className={`fa ${KIND_ICON[kind] || KIND_ICON.file} bh-file__icon bh-file__icon--${kind}`} aria-hidden='true'></i>;
}

function ViewConversationFilesModal({ conversationData, showHideViewConversationFilesModal, onPreviewImages }) {
  const conversation_id = conversationData?.id;
  const conversation_name = conversationData?.name;

  const [filesData, setFilesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [view, setView] = useState("grid");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [counts, setCounts] = useState({ all: 0, image: 0, video: 0, doc: 0 });
  const [hasAnyFiles, setHasAnyFiles] = useState(false);
  // guards against out-of-order responses when filters change quickly
  const requestIdRef = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const fetchPage = useCallback(
    async (pageToLoad, append) => {
      if (!conversation_id) return;
      const requestId = ++requestIdRef.current;
      try {
        if (append) setIsFetchingMore(true);
        else setLoading(true);
        const response = await ApiService.files.getAllConversationFilesV2(conversation_id, {
          page: pageToLoad,
          limit: PAGE_SIZE,
          type: filter,
          search: debouncedQuery,
          sort,
        });
        if (requestId !== requestIdRef.current) return;
        if (!response?.success) return showSwal(common.errors.VIEW_CONVERSATION_FILES_ERROR);
        const { files = [], pagination, counts: serverCounts } = response;
        setFilesData((prev) => {
          if (!append) return files;
          const seen = new Set(prev.map((f) => f.id));
          return [...prev, ...files.filter((f) => !seen.has(f.id))];
        });
        setPage(pageToLoad);
        setHasMore(!!pagination?.hasNextPage);
        if (serverCounts) {
          setCounts(serverCounts);
          setHasAnyFiles(serverCounts.all > 0);
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setIsFetchingMore(false);
        }
      }
    },
    [conversation_id, filter, debouncedQuery, sort]
  );

  // any change to the conversation, filter, search or sort restarts from page 1
  useEffect(() => {
    fetchPage(1, false);
  }, [fetchPage]);

  const fetchMoreData = () => {
    if (!isFetchingMore && hasMore) fetchPage(page + 1, true);
  };

  // one item per attachment
  const visible = useMemo(
    () =>
      filesData.map((file) => ({
        key: file.id,
        user: file.user,
        attachment: file.attachment || "",
        createdAt: file.createdAt,
        ...getFileKind(file.file_type, file.attachment),
      })),
    [filesData]
  );

  const groups = useMemo(() => {
    const map = new Map();
    visible.forEach((it) => {
      const label = monthLabel(new Date(it.createdAt));
      if (!map.has(label)) map.set(label, []);
      map.get(label).push(it);
    });
    return Array.from(map.entries());
  }, [visible]);

  const imageList = useMemo(() => visible.filter((it) => it.kind === "image"), [visible]);

  const openItem = (item) => {
    if (item.kind === "image" && onPreviewImages) {
      const index = imageList.findIndex((it) => it.key === item.key);
      onPreviewImages(imageList.map((it) => ({ src: fileUrl(it.attachment), name: displayName(it.attachment), type: "image" })), Math.max(index, 0));
      return;
    }
    window.open(fileUrl(item.attachment), "_blank", "noopener,noreferrer");
  };

  const toolbar = (
    <div className='bh-toolbar'>
      <div className='bh-search'>
        <i className='fa fa-search bh-search__icon' aria-hidden='true'></i>
        <input
          type='text'
          className='bh-input bh-search__input'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder='Search by file or sender…'
          aria-label='Search files'
        />
      </div>
      <div className='bh-toolbar__row'>
        <div className='bh-filters' role='tablist'>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type='button'
              role='tab'
              aria-selected={filter === f.key}
              className={`bh-filter ${filter === f.key ? "is-active" : ""}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              <span className='bh-filter__count'>{counts[f.key]}</span>
            </button>
          ))}
        </div>
        <select className='bh-select' value={sort} onChange={(e) => setSort(e.target.value)} aria-label='Sort files'>
          <option value='newest'>Newest</option>
          <option value='oldest'>Oldest</option>
        </select>
        <div className='bh-segment'>
          <button
            type='button'
            className={view === "grid" ? "is-active" : ""}
            onClick={() => setView("grid")}
            aria-label='Grid view'
          >
            <i className='fa fa-th-large' aria-hidden='true'></i>
          </button>
          <button
            type='button'
            className={view === "list" ? "is-active" : ""}
            onClick={() => setView("list")}
            aria-label='List view'
          >
            <i className='fa fa-list' aria-hidden='true'></i>
          </button>
        </div>
      </div>
    </div>
  );

  let content;
  if (loading) {
    content = <SkeletonRows count={5} />;
  } else if (visible.length === 0) {
    content = (
      <EmptyState
        icon='fa-folder-open-o'
        title={!hasAnyFiles ? "No files shared yet" : "No files match"}
        hint={!hasAnyFiles ? "Files and photos sent in this conversation appear here" : "Try another filter or search"}
      />
    );
  } else {
    const sections = groups.map(([label, group]) => (
      <section key={label}>
        <h4 className='bh-month'>{label}</h4>
        {view === "grid" ? (
          <div className='bh-grid'>
            {group.map((it) => (
              <button type='button' key={it.key} className='bh-file' onClick={() => openItem(it)} title={displayName(it.attachment)}>
                <div className='bh-file__thumb'>
                  <FileThumb item={it} />
                  {it.ext && <span className='bh-file__ext'>{it.ext}</span>}
                </div>
                <div className='bh-file__info'>
                  <div className='bh-file__name'>{displayName(it.attachment)}</div>
                  <div className='bh-file__meta'>
                    <UserAvatar user={it.user} size={18} />
                    <span>{it.user?.display_name}</span>
                  </div>
                  <div className='bh-file__meta'>
                    <span>{dateToWords(it.createdAt)}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <ul className='bh-list'>
            {group.map((it) => (
              <li key={it.key}>
                <button type='button' className='bh-row bh-row--click' onClick={() => openItem(it)}>
                  <span className='bh-file-row-thumb'>
                    <FileThumb item={it} />
                  </span>
                  <div className='bh-row__main'>
                    <div className='bh-row__name' style={{ fontSize: 14 }}>
                      {displayName(it.attachment)}
                    </div>
                    <div className='bh-row__sub'>
                      {it.user?.display_name} · {dateToWords(it.createdAt)}
                      {it.ext ? ` · ${it.ext.toUpperCase()}` : ""}
                    </div>
                  </div>
                  <i className='fa fa-external-link' aria-hidden='true' style={{ color: "#6b7785" }}></i>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    ));
    content = (
      <InfiniteScroll
        dataLength={visible.length}
        next={fetchMoreData}
        hasMore={hasMore}
        scrollableTarget={SCROLL_TARGET_ID}
        scrollThreshold='180px'
        style={{ overflow: "visible" }}
        loader={<SkeletonRows count={2} />}
      >
        {sections}
      </InfiniteScroll>
    );
  }

  return (
    <ModalShell
      title={`Files · ${counts.all}`}
      subtitle={conversation_name}
      size='lg'
      onClose={showHideViewConversationFilesModal}
      headerExtra={toolbar}
      bodyClassName='bh-modal__body--flush'
      bodyId={SCROLL_TARGET_ID}
    >
      {content}
    </ModalShell>
  );
}

export default ViewConversationFilesModal;
